import { connectivityService } from '~/data/connectivity/ConnectivityService';
import { syncManager } from '~/data/sync/SyncManager';
import { retryFailed } from '~/data/sync/syncProgressState';
import { publishSyncResult } from '~/data/sync/syncResultNotifier';

/** Wait for a steady connection before draining, so a flicker does not start/fail a run. */
export const STABLE_ONLINE_MS = 3000;

let stableTimer: ReturnType<typeof setTimeout> | null = null;
let stopWatching: (() => void) | null = null;

export function cancelScheduledDrain(): void {
  if (stableTimer) {
    clearTimeout(stableTimer);
    stableTimer = null;
  }
  if (stopWatching) {
    stopWatching();
    stopWatching = null;
  }
}

/**
 * After the network returns, drain only once `isOnline()` has stayed true for {@link STABLE_ONLINE_MS}.
 * Going offline before the timer fires resets the wait.
 */
export function scheduleDrainWhenStable(reason: string): void {
  cancelScheduledDrain();

  const arm = (): void => {
    if (stableTimer) {
      clearTimeout(stableTimer);
      stableTimer = null;
    }
    if (!connectivityService.isOnline()) {
      return;
    }
    stableTimer = setTimeout(() => {
      stableTimer = null;
      if (!connectivityService.isOnline()) {
        return;
      }
      cancelScheduledDrain();
      void drainOfflineQueueIfReady(reason);
    }, STABLE_ONLINE_MS);
  };

  stopWatching = connectivityService.subscribe(() => {
    if (!connectivityService.isOnline()) {
      if (stableTimer) {
        clearTimeout(stableTimer);
        stableTimer = null;
      }
      return;
    }
    if (!stableTimer) {
      arm();
    }
  });
}

/** Drain pending SyncOfflineData when online (Cordova SQLite present). */
export async function drainOfflineQueueIfReady(reason: string): Promise<void> {
  if (!window.sqlitePlugin || !connectivityService.isOnline()) {
    return;
  }
  if (syncManager.isDrainInProgress()) {
    return;
  }
  try {
    const response = await syncManager.drain();
    publishSyncResult(response);
  } catch (e) {
    console.error(`[Sync] drain failed (${reason}); pending data retained`, e);
  }
}

/** Re-send items marked failed. No-op while a drain is already running or nothing failed. */
export async function retryFailedSync(): Promise<void> {
  if (!window.sqlitePlugin || !connectivityService.isOnline()) {
    return;
  }
  if (syncManager.isDrainInProgress()) {
    return;
  }
  if (!retryFailed()) {
    return;
  }
  try {
    const response = await syncManager.drain({ failedOnly: true });
    publishSyncResult(response);
  } catch (e) {
    console.error('[Sync] retry of failed items failed; pending data retained', e);
  }
}
